import sanitize from "sanitize-html"
import { isExternalHref } from "@/lib/url"
import {
  isPathAllowedInCountry,
  gateForPath,
  countriesForGatedPage,
} from "@/lib/country-gates"
import type { Country } from "@/i18n/config"
import { localizedPath } from "@/lib/localized-route-slugs"

/** Internal routes that should be locale-prefixed. Matches the set used by
 *  the top-level redirect rule in next.config.ts. Anything outside this list
 *  (mailto:, https://, anchors, locale-prefixed paths) is left untouched. */
const INTERNAL_ROUTES = new Set([
  "pricing",
  "about",
  "contact",
  "how-it-works",
  "locations",
  "careers",
  "faq",
  "insurance",
  "free-call",
  "report",
  "tests",
  "insights",
  "biomarkers",
  "what-we-detect",
  "terms-conditions",
  "privacy",
  "imprint",
  "medical-data-sharing",
  "authors",
  "compare-health-check-ups-switzerland",
  "waitlist",
])

/** Rewrite bare `/tests/x`-style hrefs to `/{country}/{locale}/tests/x` so
 *  CMS-authored links follow the user's current locale instead of bouncing
 *  through the `/tests/*` → `/ch/en/*` redirect in next.config.ts. */
function rewriteHref(href: string, localePrefix: string): string {
  if (!href.startsWith("/")) return href
  const path = href.split(/[?#]/)[0]
  const first = path.replace(/^\/+/, "").split("/")[0]
  if (!first || !INTERNAL_ROUTES.has(first)) return href
  // A shared CMS answer can link to a page country-gated out of the market it's
  // served in (e.g. the CH-only insurance checker in a DE-served FAQ). Prefixing
  // with the current locale would 404, so resolve gated-out links against the
  // country where the page actually exists.
  const [, country, locale] = localePrefix.split("/")
  // Pages with a translated URL (lib/localized-route-slugs.ts) get the locale's own
  // slug — same reason we prefix at all: a CMS link shouldn't ride a redirect.
  const localized = locale ? localizedPath(href, locale) : href
  if (country && !isPathAllowedInCountry(path, country as Country)) {
    const gate = gateForPath(path)
    const canonicalCountry = gate && countriesForGatedPage(gate)[0]
    if (canonicalCountry) return `/${canonicalCountry}/${locale}${localized}`
  }
  return `${localePrefix}${localized}`
}

/**
 * Sanitize HTML strings before use with dangerouslySetInnerHTML.
 *
 * Uses sanitize-html (pure JS, no jsdom dependency) so it works reliably
 * in both Node.js server environments (Vercel serverless) and the browser.
 *
 * Pass `localePrefix` (e.g. `/ch/fr`) to also rewrite bare internal links
 * so they stay in the current locale instead of hitting the always-EN
 * redirect.
 */
export function sanitizeHtml(html: string, localePrefix?: string): string {
  return sanitize(html, {
    allowedTags: sanitize.defaults.allowedTags.concat(["img"]),
    allowedAttributes: {
      ...sanitize.defaults.allowedAttributes,
      "*": ["class"],
      ol: ["type", "start"],
      a: [...(sanitize.defaults.allowedAttributes.a ?? []), "target", "rel"],
      img: ["src", "alt", "width", "height"],
    },
    selfClosing: ["br", "hr", "img"],
    transformTags: {
      a: (tagName, attribs) => {
        if (attribs.href && localePrefix)
          attribs.href = rewriteHref(attribs.href, localePrefix)
        if (attribs.href && isExternalHref(attribs.href)) {
          attribs.target = "_blank"
          attribs.rel = "noopener noreferrer"
        }
        return { tagName, attribs }
      },
    },
  })
}
